import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { C } from '../theme';

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ username: '', password: '' });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [focused, setFocused] = useState('');
  const [showPwd, setShowPwd] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const userData = await login(form.username, form.password);
      if (userData.role === 'installer') navigate('/installer/dashboard');
      else if (userData.role === 'dealer') navigate('/dealer/dashboard');
      else navigate('/dashboard');
    } catch {
      setError('Invalid username or password. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={S.page}>
      {/* Left panel */}
      <div style={S.left}>
        <div style={S.brand}>
          <div style={S.brandIcon}>🐝</div>
          <div style={S.brandName}>HIVE ERP</div>
          <div style={S.brandTag}>Enterprise Resource Planning</div>
        </div>
        <div style={S.featureList}>
          {[
            ['📊', 'Real-time dashboards & analytics'],
            ['👥', 'Human resource management'],
            ['📦', 'Inventory & supply chain'],
            ['💰', 'Finance & invoicing'],
          ].map(([icon, text]) => (
            <div key={text} style={S.feature}>
              <span style={S.featureIcon}>{icon}</span>
              <span style={S.featureText}>{text}</span>
            </div>
          ))}
        </div>
        <div style={S.leftFooter}>Centralized · Scalable · Efficient</div>
      </div>

      {/* Right panel */}
      <div style={S.right}>
        <div style={S.card}>
          <div style={S.cardHeader}>
            <div style={S.cardTitle}>Welcome back</div>
            <div style={S.cardSub}>Sign in to your workspace</div>
          </div>

          {error && (
            <div style={S.error}>
              <span>⚠</span> {error}
            </div>
          )}

          <form onSubmit={handleSubmit}>
            <div style={S.field}>
              <label style={S.label}>Username</label>
              <input
                style={{ ...S.input, borderColor: focused === 'u' ? C.primary : C.border }}
                type="text"
                placeholder="Enter your username"
                value={form.username}
                onChange={e => setForm({ ...form, username: e.target.value })}
                onFocus={() => setFocused('u')}
                onBlur={() => setFocused('')}
                required
              />
            </div>

            <div style={S.field}>
              <label style={S.label}>Password</label>
              <div style={{ position: 'relative' }}>
                <input
                  style={{ ...S.input, borderColor: focused === 'p' ? C.primary : C.border, paddingRight: '42px' }}
                  type={showPwd ? 'text' : 'password'}
                  placeholder="Enter your password"
                  value={form.password}
                  onChange={e => setForm({ ...form, password: e.target.value })}
                  onFocus={() => setFocused('p')}
                  onBlur={() => setFocused('')}
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPwd(p => !p)}
                  style={{
                    position: 'absolute', right: '12px', top: '50%', transform: 'translateY(-50%)',
                    background: 'none', border: 'none', cursor: 'pointer',
                    padding: '2px', color: C.textSub, fontSize: '16px', lineHeight: 1,
                  }}
                  title={showPwd ? 'Hide password' : 'Show password'}
                >
                  {showPwd ? '🙈' : '👁'}
                </button>
              </div>
            </div>

            <button
              style={loading ? S.btnOff : S.btn}
              type="submit"
              disabled={loading}
            >
              {loading ? (
                <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
                  <span style={S.spinnerSmall} /> Signing in...
                </span>
              ) : 'Sign In'}
            </button>
          </form>

          <div style={S.hint}>
            <span style={{ color: C.textSub }}>Default credentials: </span>
            <code style={S.code}>admin</code>
            <span style={{ color: C.textSub }}> / </span>
            <code style={S.code}>admin@123</code>
          </div>
        </div>
      </div>
    </div>
  );
}

const S = {
  page: {
    minHeight: '100vh',
    display: 'flex',
    fontFamily: "'Segoe UI', sans-serif",
  },
  left: {
    width: '42%',
    background: `linear-gradient(160deg, ${C.sidebar} 0%, #2A1A05 60%, #3D2608 100%)`,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    padding: '60px 48px',
    position: 'relative',
    overflow: 'hidden',
  },
  brand: { marginBottom: '52px' },
  brandIcon: { fontSize: '52px', marginBottom: '12px' },
  brandName: {
    fontSize: '30px', fontWeight: 800, color: C.primary,
    letterSpacing: '3px', marginBottom: '6px',
  },
  brandTag: { fontSize: '13px', color: 'rgba(255,255,255,0.45)', letterSpacing: '1px' },
  featureList: { display: 'flex', flexDirection: 'column', gap: '18px', marginBottom: '52px' },
  feature: { display: 'flex', alignItems: 'center', gap: '14px' },
  featureIcon: {
    width: '38px', height: '38px', borderRadius: '10px',
    background: 'rgba(245,166,35,0.15)',
    border: '1px solid rgba(245,166,35,0.25)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    fontSize: '17px', flexShrink: 0,
  },
  featureText: { fontSize: '13px', color: 'rgba(255,255,255,0.7)', fontWeight: 400 },
  leftFooter: {
    fontSize: '11px', color: 'rgba(255,255,255,0.2)',
    letterSpacing: '2px', textTransform: 'uppercase',
  },
  right: {
    flex: 1,
    background: C.primaryBg,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '40px 32px',
  },
  card: {
    background: C.white,
    borderRadius: '16px',
    padding: '44px 40px',
    width: '100%',
    maxWidth: '400px',
    boxShadow: '0 4px 24px rgba(245,166,35,0.08), 0 1px 4px rgba(0,0,0,0.06)',
    border: `1px solid ${C.border}`,
  },
  cardHeader: { marginBottom: '28px' },
  cardTitle: { fontSize: '24px', fontWeight: 700, color: C.textMain, marginBottom: '4px' },
  cardSub: { fontSize: '13px', color: C.textSub },
  error: {
    background: C.dangerBg,
    color: C.dangerText,
    border: `1px solid #FECACA`,
    borderRadius: '8px',
    padding: '10px 14px',
    marginBottom: '20px',
    fontSize: '13px',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  field: { marginBottom: '18px' },
  label: { display: 'block', marginBottom: '7px', fontSize: '12px', fontWeight: 600, color: C.textMain, letterSpacing: '0.3px' },
  input: {
    width: '100%', padding: '11px 14px',
    border: '1.5px solid', borderRadius: '8px',
    fontSize: '14px', outline: 'none', boxSizing: 'border-box',
    color: C.textMain, background: C.white,
    transition: 'border-color 0.15s',
  },
  btn: {
    width: '100%', padding: '13px',
    background: `linear-gradient(135deg, ${C.primary}, ${C.primaryDark})`,
    color: '#fff', border: 'none', borderRadius: '9px',
    fontSize: '14px', fontWeight: 700, cursor: 'pointer',
    marginTop: '6px', letterSpacing: '0.4px',
    boxShadow: `0 4px 14px rgba(245,166,35,0.35)`,
    transition: 'opacity 0.15s',
  },
  btnOff: {
    width: '100%', padding: '13px',
    background: '#E5E7EB', color: '#9CA3AF',
    border: 'none', borderRadius: '9px',
    fontSize: '14px', fontWeight: 700, cursor: 'not-allowed',
    marginTop: '6px',
  },
  spinnerSmall: {
    width: '14px', height: '14px',
    border: '2px solid rgba(255,255,255,0.3)',
    borderTopColor: '#fff',
    borderRadius: '50%',
    display: 'inline-block',
    animation: 'spin 0.7s linear infinite',
  },
  hint: { marginTop: '22px', fontSize: '12px', textAlign: 'center' },
  code: {
    background: C.primaryLight, color: C.primaryDeep,
    padding: '1px 6px', borderRadius: '4px',
    fontSize: '12px', fontWeight: 600,
  },
};
